import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCart } from '../context/CartContext';
import { DELIVERY_MINIMUM_ORDER, deliveryShortfall } from '../utils/deliveryMinimum';
import '../stylings/BucksHeader.css';
import {
  getItemEffectiveUnitPrice,
  getItemLineTotal,
  getItemSavings,
  getSpecialPricingDescription,
  getUnitLabel
} from '../utils/pricing';

const BucksSearch = lazy(() => import('./BucksSearch'));

function BucksHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 768);
  const cartContentRef = useRef(null);
  const cartButtonRef = useRef(null);
  
  // Get cart data from context instead of local state
  const { cartItems, removeFromCart, itemCount, subtotal } = useCart();
  const amountToDelivery = deliveryShortfall(subtotal);
  
  // Get current path for active link handling
  const currentPath = window.location.pathname;

  useEffect(() => {
    // Add scroll event listener
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    document.addEventListener('scroll', handleScroll);
    
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Add scroll detection for better visual feedback
  useEffect(() => {
    const cartItemsElement = document.querySelector('.cart-items');
    const cartDropdown = document.querySelector('.cart-dropdown');
    
    if (cartItemsElement && cartDropdown) {
      const checkScroll = () => {
        if (cartItemsElement.scrollHeight > cartItemsElement.clientHeight) {
          cartDropdown.classList.add('has-scroll');
        } else {
          cartDropdown.classList.remove('has-scroll');
        }
      };
      
      // Check on cart content change
      const observer = new MutationObserver(checkScroll);
      observer.observe(cartItemsElement, { childList: true, subtree: true });
      
      // Initial check
      checkScroll();
      
      return () => observer.disconnect();
    }
  }, [cartOpen, cartItems]);

  // This hook centrally manages body scrolling when any overlay is open.
  useEffect(() => {
    // If any overlay is open, disable body scroll to improve user experience
    if (menuOpen || cartOpen || searchOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  
    // Cleanup function to restore scroll when the component unmounts
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [menuOpen, cartOpen, searchOpen]); // Reruns whenever an overlay's state changes

  // Close cart when clicking/tapping anywhere outside of cart content.
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      const clickedInsideCart = cartContentRef.current?.contains(target);
      const clickedCartButton = cartButtonRef.current?.contains(target);

      if (!clickedInsideCart && !clickedCartButton) {
        setCartOpen(false);
      }
    };

    // Handle escape key to close cart
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && cartOpen) {
        setCartOpen(false);
      }
    };

    if (cartOpen) {
      document.addEventListener('pointerdown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [cartOpen]);

  // Close mobile menu when tapping/clicking outside of it.
  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const clickedInsideMenu = Boolean(target.closest('.main-nav'));
      const clickedMenuToggle = Boolean(target.closest('.mobile-menu-toggle'));

      if (!clickedInsideMenu && !clickedMenuToggle) {
        setMenuOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [menuOpen]);

  // Track the mobile breakpoint so cart overlay can be portaled outside header stacking contexts.
  useEffect(() => {
    const mobileMediaQuery = window.matchMedia('(max-width: 768px)');

    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mobileMediaQuery.matches);

    if (typeof mobileMediaQuery.addEventListener === 'function') {
      mobileMediaQuery.addEventListener('change', handleViewportChange);
    } else {
      mobileMediaQuery.addListener(handleViewportChange);
    }

    return () => {
      if (typeof mobileMediaQuery.removeEventListener === 'function') {
        mobileMediaQuery.removeEventListener('change', handleViewportChange);
      } else {
        mobileMediaQuery.removeListener(handleViewportChange);
      }
    };
  }, []);

  // Reset global overflow lock state when the page is cached/unloaded.
  useEffect(() => {
    const handlePageHide = () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      handlePageHide();
    };
  }, []);

  // Check if a link should be shown based on current path
  const shouldShowLink = (path) => {
    // Don't show Home link on homepage
    if (path === '/' && (currentPath === '/' || currentPath === '')) return false;
    // Don't show Shop link on shop page
    if (path === '/shop' && currentPath === '/shop') return false;
    // Don't show Buck's Box link on Buck's Box page
    if (path === '/bucks-box' && currentPath === '/bucks-box') return false;
    // Don't show Our Story link on about page
    if (path === '/about' && currentPath === '/about') return false;
    // Don't show Community link on community page
    if (path === '/community' && currentPath === '/community') return false;
    // Don't show Recipes link on recipes page
    if (path === '/recipes' && currentPath === '/recipes') return false;
    // Don't show FAQ link on FAQ page
    if (path === '/faq' && currentPath === '/faq') return false;
    // Don't show Contact link on contact page
    if (path === '/contact' && currentPath === '/contact') return false;
    
    return true;
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (cartOpen) setCartOpen(false);
    if (searchOpen) setSearchOpen(false);
  };

  const toggleCart = () => {
    setCartOpen(!cartOpen);
    if (menuOpen) setMenuOpen(false);
    if (searchOpen) setSearchOpen(false);
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    if (menuOpen) setMenuOpen(false);
    if (cartOpen) setCartOpen(false);
  };

  // Close search overlay
  const closeSearch = () => {
    setSearchOpen(false);
  };

  // Close cart dropdown when navigating away
  const closeCart = () => {
    setCartOpen(false);
  };

  // Enhanced calculate total using effective unit prices for special pricing
  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + getItemLineTotal(item);
    }, 0).toFixed(2);
  };
  
  // Inline SVG icons as a fallback solution
  const searchIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
  
  const basketIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h2l.6 3m0 0h16.8M4.6 6l1.2 6m0 0h12.4m-12.4 0l.8 4h10.8l.8-4m-12.4 0L6 6M18 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM10 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"></path>
    </svg>
  );

  // Reusable cart content component
  const CartContent = () => (
    <>
      <div className="cart-header">
        <h3>Your Basket</h3>
        <button
          className="cart-close-btn"
          onClick={() => setCartOpen(false)}
          aria-label="Close cart"
        >
          ✕
        </button>
      </div>
      
      {(!cartItems || cartItems.length === 0) ? (
        <p className="empty-cart">Your basket is empty</p>
      ) : (
        <>
          <ul className="cart-items">
            {cartItems.map(item => (
              <li key={item.id} className="cart-item">
                <div className="item-details">
                  <span className="item-name">{item.name}</span>
                  <div className="item-quantity">
                    {item.hasSpecialPricing && item.specialPricingInfo ? (
                      <div className="special-pricing-display">
                        <span className="dozen-display">{getSpecialPricingDescription(item)}</span>
                        <div className="pricing-info">
                          <span className="effective-price">
                            Qty: {item.quantity} @ ${getItemEffectiveUnitPrice(item).toFixed(2)} / {getUnitLabel(item.unit)}
                          </span>
                          {getItemSavings(item) > 0 && (
                            <span className="savings-indicator">
                              Save ${getItemSavings(item).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="item-quantity-details">
                        <span>Qty: {item.quantity}</span>
                        <span>@</span>
                        <span>${getItemEffectiveUnitPrice(item).toFixed(2)} / {getUnitLabel(item.unit)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <span className="item-price">
                  ${getItemLineTotal(item).toFixed(2)}
                </span>
                <button 
                  className="remove-btn" 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFromCart(item.id);
                  }}
                  aria-label="Remove item"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <div className="cart-total">
            <span>Total</span>
            <span>${subtotal ? subtotal.toFixed(2) : calculateTotal()}</span>
          </div>
          {amountToDelivery > 0 && (
            <div className="cart-minimum-note" role="status">
              <span>Add <strong>${amountToDelivery.toFixed(2)}</strong> more for the ${DELIVERY_MINIMUM_ORDER.toFixed(2)} delivery minimum.</span>
              <a href="/shop" onClick={closeCart}>Add items</a>
            </div>
          )}
          <div className="cart-actions">
            <button 
              className="view-cart-btn" 
              onClick={() => {
                closeCart();
                window.location.href = '/basket';
              }}
            >
              View Basket
            </button>
            <button 
              className="checkout-btn"
              disabled={amountToDelivery > 0}
              onClick={() => {
                closeCart();
                window.location.href = '/checkout';
              }}
            >
              Checkout
            </button>
          </div>
        </>
      )}
    </>
  );

  const cartDropdown = (
    <div
      className={`cart-dropdown ${cartOpen ? 'open' : 'closed'} ${isMobileViewport ? 'cart-dropdown-mobile-portal' : ''}`}
      aria-hidden={!cartOpen}
    >
      <div ref={cartContentRef} className="cart-content">
        <CartContent />
      </div>
    </div>
  );

  return (
    <>
      {menuOpen && <div className="mobile-menu-backdrop" onClick={() => setMenuOpen(false)} aria-hidden="true" />}
      <header className={`bucks-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <div className="logo-container">
            <a href="/" className="logo-link">
              <div className="logo">
                <span className="logo-text">Buck's Farmstand</span>
              </div>
            </a>
          </div>

          <nav className={`main-nav ${menuOpen ? 'open' : ''}`}>
            <ul className="nav-links">
              {shouldShowLink('/') && <li><a href="/">Home</a></li>}
              {shouldShowLink('/shop') && <li><a href="/shop">Shop</a></li>}
              {shouldShowLink('/bucks-box') && <li><a href="/bucks-box">Buck's Box</a></li>}
              {shouldShowLink('/about') && <li><a href="/about">Our Story</a></li>}
              {shouldShowLink('/community') && <li><a href="/community">Community</a></li>}
              {shouldShowLink('/recipes') && <li><a href="/recipes">Recipes</a></li>}
              {shouldShowLink('/faq') && <li><a href="/faq">FAQ</a></li>}
              {shouldShowLink('/contact') && <li><a href="/contact">Contact</a></li>}
            </ul>
          </nav>

          <div className="header-actions">
            <button 
              className="search-button" 
              onClick={toggleSearch} 
              aria-label="Search"
            >
              {searchIcon}
            </button>
            
            <div className="cart-container">
              <button ref={cartButtonRef} className="cart-button" onClick={toggleCart} aria-label="Shopping Basket">
                {basketIcon}
                {(cartItems && cartItems.length > 0) && (
                  <span className="cart-count">{itemCount || cartItems.length}</span>
                )}
              </button>

              {!isMobileViewport && cartDropdown}
            </div>

            <button className="mobile-menu-toggle" onClick={toggleMenu} aria-label="Toggle Menu">
              <span className={`menu-bar ${menuOpen ? 'open' : ''}`}></span>
            </button>
          </div>
        </div>
      </header>

      {/* Search Component */}
      {isMobileViewport && createPortal(cartDropdown, document.body)}

      {searchOpen && (
        <Suspense fallback={null}>
          <BucksSearch isOpen={searchOpen} onClose={closeSearch} />
        </Suspense>
      )}
    </>
  );
}

export default BucksHeader;
